import styled, { css, FlattenSimpleInterpolation } from "styled-components";
import { FC } from "react";

import { WhiteIcon } from "./WhiteIcon";
import { SemanticIcon } from "./SemanticIcon";

import {
  AllIconName,
  IconName,
  IconType,
  IconVariant,
  SemanticIconName,
} from "./types";
import { SizedIcon } from "./SizedIcon";
import { BaseIcon } from "./BaseIcon";
import { BaseComponentProps } from "../types";
import { WhiteIconVariant } from "./types";
import { IconSize } from "../Icon";

type StyledIconProps = {
  $variant: IconVariant;
  $plain?: boolean;
};

const StyledIcon = styled(SizedIcon)<StyledIconProps>`
  ${({ $variant, $plain }): FlattenSimpleInterpolation => {
    switch ($variant) {
      case "DISABLED":
        return css`
          svg {
            --main-color: var(--color-primary-200);
          }
        `;
      case "DANGER":
        return $plain
          ? css`
              svg {
                --main-color: var(--color-error-600);
              }
            `
          : css`
              svg {
                --main-color: var(--color-error-600);
                :hover {
                  --main-color: var(--color-error-500);
                }
              }
            `;

      case "ACTIVE":
        return css`
          svg {
            --main-color: var(--color-secondary-800);
          }
        `;

      case "INACTIVE":
        return css`
          svg {
            --main-color: var(--color-primary-400);
          }
        `;

      case "DEFAULT":
      default:
        return $plain
          ? css`
              svg {
                --main-color: var(--color-primary-800);
              }
            `
          : css`
              svg {
                --main-color: var(--color-primary-800);
                :hover {
                  --main-color: var(--color-secondary-600);
                }
              }
            `;
    }
  }}
`;

type IconProps = BaseComponentProps<
  "svg",
  {
    /** The icon that will be rendered. */
    iconName: AllIconName;

    /** The size of icon that will be rendered. */
    iconSize?: IconSize;

    /** The variation of icon that will be rendered. */
    variant?: IconVariant;

    /** This variable disables the hover effect on default. */
    plain?: boolean;

    /** The type of icon rendered. */
    type?: IconType;
  }
>;

export const Icon: FC<IconProps> = ({
  iconSize = "regular",
  variant = "DEFAULT",
  type = "default",
  className,
  plain,
  ...props
}) => {
  switch (type) {
    case "semantic":
      return (
        <SemanticIcon
          {...props}
          iconName={props.iconName as SemanticIconName}
          iconSize={iconSize}
          className={className}
        />
      );
    case "white":
      return (
        <WhiteIcon
          {...props}
          iconName={props.iconName as IconName}
          iconSize={iconSize}
          variant={variant as WhiteIconVariant}
          plain={plain}
          className={className}
        />
      );
    default:
      return (
        <StyledIcon
          className={className}
          $iconSize={iconSize}
          $variant={variant}
          $plain={plain}
        >
          <BaseIcon disabled={variant === "DISABLED"} {...props} />
        </StyledIcon>
      );
  }
};

// TODO: Remove this component as soon as possible. Use the prop `iconSize` instead.

export const SmallIcon: FC<IconProps> = ({
  iconSize = "small",
  variant = "DEFAULT",
  className,
  plain,
  ...props
}) => (
  <StyledIcon
    className={className}
    $iconSize={iconSize}
    $variant={variant}
    $plain={plain}
  >
    <BaseIcon disabled={variant === "DISABLED"} {...props} />
  </StyledIcon>
);
